export type Value = string | number | boolean | null;
export type Fields = Record<string, Value>;
export interface SourceRecord { source_table: string; fields: Fields; }
export interface Page { items: SourceRecord[]; returned: number; has_more: boolean; next_cursor: string | null; coverage: string; }
export interface Issue { code: string; severity: string; message: string; }
export interface Envelope<T> { ok: boolean; data: T; meta: { source_instance_id: string; captured_at_utc: string }; issues: Issue[]; }
export interface Resolution { requested_uuid: string; load_key: string; evidence: string; }
export interface Details { load_key: string; load: SourceRecord; order: SourceRecord; additional: Page; }
export interface Notes { load_key: string; load: SourceRecord | null; order: SourceRecord | null; additional: Page; mapping: Page | null; history: Page; }
export type PartyRole = 'customer' | 'carrier' | 'shipper' | 'consignee';
export interface Parties { load_key: string; order_snapshot: SourceRecord; load_snapshot: SourceRecord; parties: Record<PartyRole, { legacy_id: string; master: SourceRecord | null }>; }
export interface Financials {
  load_key: string;
  identity: { warnings: Issue[] };
  customer: { currency: string | null; lines: { description: string; amount_cents: number; multiplier: number | null; unit_amount_cents: number | null; taxable: boolean | null }[];
    subtotal_cents: number; tax_total_cents: number; total_cents: number; warnings: Issue[] };
  carrier: { converted_currency: string | null; converted_total_cents: number; native_totals_by_currency: Record<string, number>;
    lines: { source_record_id: string; service_name: string; carrier_name: string | null; native_currency: string | null; native_expense_cents: number; converted_expense_cents: number; quantity: number | null; native_unit_price_cents: number | null; carrier_reference: string | null; audited: boolean }[]; warnings: Issue[] };
}
export interface ResourceData {
  resolve: Resolution; load: Fields; details: Details; loads: Page; notes: Notes; parties: Parties; financials: Financials;
  history: Page; additional: Page; mapping: Page; purchase_orders: Page; releases: Page; dimensions: Page; edi_214: Page; edi_status: Page;
}
export type Resource = keyof ResourceData;
export type Section = 'overview' | 'notes' | 'parties' | 'financials' | 'references' | 'history' | 'events';
export interface CardField { label: string; value: string; }
export interface NoteCard { title: string; scope: string; body: string; }
export interface PanelState { loading: boolean; error: string; response?: Envelope<unknown>; }

export function text(value: Value | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const result = String(value).trim();
  return /^[:\s]+$/.test(result) ? '' : result;
}
export function money(cents: number | null | undefined, currency: string | null): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return 'Not recorded';
  const amount = (cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${currency || 'Currency unspecified'} ${amount}`;
}
export function fields(record: SourceRecord | null | undefined, labels: Record<string, string>): CardField[] {
  return Object.entries(labels).map(([key, label]) => ({ label, value: text(record?.fields[key]) })).filter(item => item.value !== '');
}
export function parseLoadKey(key: string): { wo: string; load: string; id: string } {
  const parts = key.split(':');
  if (parts.length !== 3 || !/^[a-zA-Z0-9]{1,6}$/.test(parts[0]) || !/^[a-zA-Z0-9]{1,2}$/.test(parts[1]) || !parts[2]) throw new Error('Invalid load identity returned by UFOs.');
  return { wo: parts[0], load: parts[1], id: parts[2] };
}
export function appendPage(previous: Page | undefined, page: Page, append: boolean): Page {
  return { ...page, items: append && previous ? [...previous.items, ...page.items] : page.items };
}
